/**
 * /places — editorial list with filtering, search, sort, infinite scroll.
 *
 * URL params: ?category=<id>&status=<status>&tag=<id>&q=<search>&sort=<sort>
 *
 * Filters update URL params; URL params drive query refetch.
 * Search input is debounced 250ms and sticky under the page header.
 * Results are a card list (NOT a dashboard table).
 * Cursor-based infinite scroll loads 24 cards per page.
 */
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  CategoryId,
  PlaceStatus,
  PlaceSummary,
  TagId,
} from '@shared/types';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { TextInput } from '../../components/ui/TextInput';
import { CategoryChip } from '../../components/ui/CategoryChip';
import { TagChip } from '../../components/ui/TagChip';
import { StatusPill } from '../../components/ui/StatusPill';
import { DropdownMenu } from '../../components/ui/DropdownMenu';
import { Sheet } from '../../components/ui/Sheet';
import { PlaceCard } from '../../components/PlaceCard';
import { AddPlaceForm } from '../../components/AddPlaceForm';
import { useActiveHouseholdId } from '../../lib/household';
import {
  useCategories,
  usePlaces,
  useTags,
  type PlacesFilters,
} from '../../lib/queries';
import { apiFetch } from '../../lib/api';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '../../lib/store';
import { ArrowsDownUp, MagnifyingGlass, Plus } from '@phosphor-icons/react';
import { cn } from '../../lib/cn';

const STATUS_OPTIONS: PlaceStatus[] = [
  'go-to',
  'untried',
  'neutral',
  'avoid',
];

const SORT_LABELS: Record<NonNullable<PlacesFilters['sort']>, string> = {
  recent_visit: 'Recently visited',
  recent_added: 'Recently added',
  name_asc: 'A → Z',
  rating_desc: 'Highest rated',
  status: 'Status',
};
const SORT_OPTIONS = Object.keys(SORT_LABELS) as Array<
  keyof typeof SORT_LABELS
>;

function arrayParam<T extends string>(
  params: URLSearchParams,
  key: string,
): T[] {
  return params.getAll(key) as T[];
}

interface PaginatedPlacesResponse {
  places: PlaceSummary[];
  next_cursor: string | null;
}

export default function PlacesListRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const householdId = useActiveHouseholdId();
  const categories = useCategories(householdId);
  const tagsQ = useTags(householdId);
  const openCapture = useAppStore((s) => s.openCaptureSheet);

  const [addOpen, setAddOpen] = React.useState(false);

  // URL → filter state
  const categoryIds = arrayParam<CategoryId>(searchParams, 'category');
  const statuses = arrayParam<PlaceStatus>(searchParams, 'status');
  const tagIds = arrayParam<TagId>(searchParams, 'tag');
  const q = searchParams.get('q') ?? '';
  const sortParam = (searchParams.get('sort') ??
    'recent_visit') as keyof typeof SORT_LABELS;

  // Local input mirror for debounced search.
  const [queryInput, setQueryInput] = React.useState(q);
  React.useEffect(() => setQueryInput(q), [q]);
  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      if (queryInput === q) return;
      const next = new URLSearchParams(searchParams);
      if (queryInput.trim()) next.set('q', queryInput.trim());
      else next.delete('q');
      setSearchParams(next, { replace: true });
    }, 250);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryInput]);

  const baseFilters: PlacesFilters = React.useMemo(() => {
    const f: PlacesFilters = {
      ...(categoryIds.length ? { category: categoryIds } : {}),
      ...(statuses.length ? { status: statuses } : {}),
      ...(tagIds.length ? { tag: tagIds } : {}),
      ...(q ? { q } : {}),
      sort: sortParam,
      limit: 24,
    };
    return f;
  }, [
    categoryIds.join(','),
    statuses.join(','),
    tagIds.join(','),
    q,
    sortParam,
  ]);

  const initial = usePlaces(householdId, baseFilters);

  // Manual cursor-based infinite scroll — accumulate pages.
  const [pages, setPages] = React.useState<PaginatedPlacesResponse[]>([]);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  // Reset accumulated pages whenever filters change.
  const filtersKey = JSON.stringify(baseFilters);
  React.useEffect(() => {
    setPages([]);
  }, [filtersKey]);

  React.useEffect(() => {
    if (initial.data && pages.length === 0) {
      setPages([initial.data]);
    }
  }, [initial.data, pages.length]);

  const lastPage = pages[pages.length - 1] ?? null;
  const allPlaces = pages.flatMap((p) => p.places);

  async function loadMore() {
    if (loadingMore) return;
    if (!lastPage?.next_cursor) return;
    setLoadingMore(true);
    try {
      const next = await apiFetch<PaginatedPlacesResponse>('/places', {
        query: {
          ...(baseFilters.category !== undefined ? { category: baseFilters.category as string | string[] } : {}),
          ...(baseFilters.status !== undefined ? { status: baseFilters.status as string | string[] } : {}),
          ...(baseFilters.tag !== undefined ? { tag: baseFilters.tag as string | string[] } : {}),
          ...(baseFilters.q ? { q: baseFilters.q } : {}),
          ...(baseFilters.sort ? { sort: baseFilters.sort } : {}),
          cursor: lastPage.next_cursor,
          limit: 24,
        },
      });
      setPages((prev) => [...prev, next]);
    } catch {
      // Surface to toast via global error pipeline (TODO once wired).
    } finally {
      setLoadingMore(false);
    }
  }

  // Intersection Observer — auto-load next page when sentinel hits viewport.
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void loadMore();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.next_cursor, filtersKey]);

  // Filter mutators.
  function toggleArrayParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    const existing = next.getAll(key);
    next.delete(key);
    if (existing.includes(value)) {
      for (const v of existing) if (v !== value) next.append(key, v);
    } else {
      for (const v of existing) next.append(key, v);
      next.append(key, value);
    }
    setSearchParams(next);
  }

  function setSort(s: string) {
    const next = new URLSearchParams(searchParams);
    next.set('sort', s);
    setSearchParams(next);
  }

  function clearFilters() {
    const next = new URLSearchParams();
    setSearchParams(next, { replace: true });
    setQueryInput('');
  }

  const hasAnyFilters =
    categoryIds.length > 0 ||
    statuses.length > 0 ||
    tagIds.length > 0 ||
    Boolean(q);

  const catList = categories.data?.categories ?? [];
  const catNameById = new Map(catList.map((c) => [c.id, c]));
  const tagList = tagsQ.data?.tags ?? [];

  const isFirstLoading = initial.isPending && pages.length === 0;

  return (
    <>
      <PageHeader
        eyebrow="Places"
        title="Your places."
        description="Filter by category or status, or search by name."
        actions={
          <Button
            variant="secondary"
            onClick={() => setAddOpen(true)}
          >
            <Plus size={16} weight="regular" aria-hidden />
            Add manually
          </Button>
        }
      />

      <div className="sticky top-0 z-20 bg-bg/95 backdrop-blur-sm -mx-5 md:-mx-8 px-5 md:px-8 pt-2 pb-4 mb-8 border-b border-ink-100">
        <label htmlFor="places-search" className="block mb-4">
          <span className="sr-only">Search places</span>
          <div className="relative">
            <MagnifyingGlass
              aria-hidden
              size={16}
              weight="regular"
              className="absolute left-0 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <TextInput
              id="places-search"
              type="search"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder="Search your journal"
              className="pl-6"
              autoComplete="off"
            />
          </div>
        </label>

        <div className="space-y-3">
          {catList.length ? (
            <div className="flex flex-wrap gap-2">
              {catList.map((c) => {
                const active = categoryIds.includes(c.id);
                return (
                  <CategoryChip
                    key={c.id}
                    active={active}
                    onClick={() => toggleArrayParam('category', c.id)}
                    aria-label={`Filter by ${c.name}`}
                  >
                    {c.name}
                  </CategoryChip>
                );
              })}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 items-center">
            {STATUS_OPTIONS.map((s) => {
              const active = statuses.includes(s);
              return (
                <StatusPill
                  key={s}
                  status={s}
                  interactive
                  pressed={active}
                  onClick={() => toggleArrayParam('status', s)}
                />
              );
            })}

            <div className="ml-auto flex items-center gap-3">
              {hasAnyFilters ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
                >
                  Clear filters
                </button>
              ) : null}

              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'inline-flex items-center gap-2 px-3 py-1 border border-ink-200',
                      'font-sans text-xs uppercase tracking-[0.12em] text-ink-700',
                      'hover:bg-surface-2 transition-colors duration-200 ease-out-slow',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
                    )}
                    aria-label="Sort places"
                  >
                    <ArrowsDownUp size={12} weight="regular" aria-hidden />
                    <span>{SORT_LABELS[sortParam]}</span>
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  {SORT_OPTIONS.map((s) => (
                    <DropdownMenu.Item
                      key={s}
                      onSelect={() => setSort(s)}
                      role="option"
                      aria-selected={sortParam === s}
                    >
                      {SORT_LABELS[s]}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </div>
          </div>

          {tagList.length ? (
            <div className="flex flex-wrap gap-2 items-center pt-1">
              <span className="text-[0.65rem] uppercase tracking-widest text-ink-500 mr-1">
                Tags
              </span>
              {tagList.map((t) => {
                const active = tagIds.includes(t.id);
                return (
                  <TagChip
                    key={t.id}
                    label={t.name}
                    active={active}
                    onClick={() => toggleArrayParam('tag', t.id)}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      {isFirstLoading ? (
        <PlacesListSkeleton />
      ) : initial.error ? (
        <EmptyState
          title="We couldn't load your places."
          description="Check your connection and try again."
          cta={
            <Button variant="primary" onClick={() => void initial.refetch()}>
              Try again
            </Button>
          }
        />
      ) : allPlaces.length === 0 ? (
        hasAnyFilters ? (
          <EmptyState
            title="No places match these filters."
            description="Try a broader filter or clear them all."
            cta={
              <Button variant="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="Your journal is empty."
            description="Capture an entry to begin, or add a place by hand."
            cta={
              <div className="flex items-center gap-3 justify-center">
                <Button variant="primary" onClick={openCapture}>
                  Capture an entry
                </Button>
                <Button variant="secondary" onClick={() => setAddOpen(true)}>
                  Add manually
                </Button>
              </div>
            }
          />
        )
      ) : (
        <>
          <ul className="space-y-0">
            {allPlaces.map((p) => (
              <li key={p.id}>
                <PlaceCard
                  place={p}
                  category={catNameById.get(p.category_id) ?? null}
                />
              </li>
            ))}
          </ul>
          <div ref={sentinelRef} className="h-8" aria-hidden />
          {loadingMore ? (
            <div className="py-6" data-testid="places-loading-more">
              <Skeleton className="h-6 w-1/2 mb-3" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          ) : !lastPage?.next_cursor && allPlaces.length > 0 ? (
            <p className="py-8 text-center text-xs uppercase tracking-widest text-ink-500">
              No more entries
            </p>
          ) : null}
        </>
      )}

      <Sheet
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add a place"
        description="Add a place by hand. You can still capture entries against it later."
        side="right"
      >
        <header className="mb-6">
          <p className="font-sans text-xs uppercase tracking-[0.14em] text-ink-500">
            New place
          </p>
          <h2 className="mt-1 font-serif text-2xl text-ink-900 leading-tight">
            Add a place by hand.
          </h2>
        </header>
        <AddPlaceForm
          onSuccess={() => setAddOpen(false)}
          onCancel={() => setAddOpen(false)}
        />
      </Sheet>
    </>
  );
}

function PlacesListSkeleton() {
  return (
    <div data-testid="places-skeleton">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="py-6 border-b border-ink-200">
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-7 w-2/3 mb-3" />
          <div className="flex gap-3">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      ))}
    </div>
  );
}

export { PlacesListSkeleton };
// Reference to unused import to silence ts-noUnused; useQuery is invoked indirectly via apiFetch but
// kept here in case future refactor moves loadMore into a useQuery.
export const __useQueryRef: typeof useQuery = useQuery;
